import { useState } from 'react';
import styled from '@/styles';
import Popover from '@/components/Popover';
import { DependabilityV2 } from '@/services/dependabilityV2API';
import { dependabilityV2Colors } from '@/styles/colors';
import { categoryColor } from './tierColors';
import PopoverSurface from './PopoverSurface';
import { CheckCircleIcon, ExclamationTriangleIcon } from './icons';

const Row = styled('div', {
  display: 'flex',
  gap: '12px',
  flexWrap: 'wrap',
  alignItems: 'center',
});
const BadgeChip = styled('span', {
  // UA-button resets FIRST (the `font` shorthand would otherwise clobber the
  // fontSize/fontWeight below): the pools chip renders `as="button"` so it is
  // reachable by keyboard; these are visual no-ops for the span usages.
  background: 'none',
  font: 'inherit',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 8px',
  borderRadius: '8px',
  fontSize: '$sm',
  fontWeight: 700,
  border: '1px solid transparent',
  variants: {
    kind: {
      positive: {
        color: dependabilityV2Colors.depPositive,
        borderColor: dependabilityV2Colors.depFeedPositive,
      },
      danger: {
        color: dependabilityV2Colors.depTierMonitored,
        backgroundColor: dependabilityV2Colors.depDangerBg,
      },
      warning: {
        color: dependabilityV2Colors.depWarnText,
        backgroundColor: dependabilityV2Colors.depWarnBg,
      },
      // "0 preferred pools" is neither good nor bad — an explicit variant so
      // the empty state is a deliberate grey chip rather than whatever the
      // unstyled base happens to inherit.
      neutral: {
        color: dependabilityV2Colors.depInk,
        borderColor: dependabilityV2Colors.depBorder,
      },
    },
  },
});
const PoolList = styled(PopoverSurface, { padding: '12px', minWidth: '200px' });

const StatusIcon = ({ positive }: { positive: boolean }) =>
  positive ? <CheckCircleIcon /> : <ExclamationTriangleIcon />;

interface Props {
  badges: DependabilityV2['badges'];
}

const StatusBadgeRow = ({ badges }: Props) => {
  const [poolsOpen, setPoolsOpen] = useState(false);
  const noShowPositive = badges.no_shows_count === 0;
  const latePositive = badges.late_count === 0;
  // GRAVY-10402: all-time late-drop (late cancellation) count. Defaulted so a
  // payload from a backend that predates the field renders "0 late cancels"
  // rather than "undefined late cancels".
  const lateCancelsCount = badges.late_cancels_count ?? 0;
  const lateCancelsPositive = lateCancelsCount === 0;
  // preferred_pools can list the same customer twice (favorited AND LTA — the
  // backend has no unique index across kinds), which inflates the "N preferred
  // pools" count and collides React keys. Collapse to one entry per customer_id.
  const uniquePools = Array.from(
    new Map(
      badges.preferred_pools.map((pool) => [pool.customer_id, pool]),
    ).values(),
  );

  return (
    <Row>
      <BadgeChip kind={noShowPositive ? 'positive' : 'danger'}>
        <StatusIcon positive={noShowPositive} />
        {/* GRAVY-10402: ALL-TIME no-show count (not a 30-day window), so it
            reconciles with the lifetime "No Shows" count shown in Worker Details
            on the same screen. "No no-shows" therefore reads as the true total,
            not a rolling window that could hide an older no-show. */}
        {noShowPositive
          ? 'No no-shows'
          : `${badges.no_shows_count} no-show${
              badges.no_shows_count === 1 ? '' : 's'
            }`}
      </BadgeChip>
      <span style={{ position: 'relative' }}>
        {/* A real <button> (fix round 3): the pool list was unreachable by
            keyboard as a styled span with onClick. */}
        {/* Zero pools is an ABSENCE, not an achievement: styled positive with a
            green check it read as a good signal, sitting beside two badges
            where green genuinely means "clean". Neutral when there are none. */}
        <BadgeChip
          as="button"
          type="button"
          kind={uniquePools.length > 0 ? 'positive' : 'neutral'}
          css={{ cursor: 'pointer' }}
          // GRAVY-10233: the SAME clickout race that hid the tier popover (see
          // TierPill). Popover arms a window-level `useClickout` the instant it
          // opens; without stopPropagation the very click that opens this popover
          // keeps bubbling to window, the clickout listener fires, and the
          // popover closes ~1ms after it opened — so a real user click flashed it
          // open then shut ("clicking does nothing"; reproduced on live QA, where
          // a real .click() failed to open it while dispatchEvent did). Toggling
          // here preserves click-again-to-close (previously provided by the same
          // window clickout that now can't see this click).
          onClick={(e) => {
            e.stopPropagation();
            setPoolsOpen((open) => !open);
          }}
          aria-haspopup="dialog"
          aria-expanded={poolsOpen}
          aria-controls="preferred-pools-popover"
          data-testid="preferred-pools-badge"
        >
          {uniquePools.length > 0 && <StatusIcon positive />}
          {`${uniquePools.length} preferred pool${
            uniquePools.length === 1 ? '' : 's'
          }`}
        </BadgeChip>
        <Popover
          id="preferred-pools-popover"
          isOpen={poolsOpen}
          onRequestClose={() => setPoolsOpen(false)}
        >
          <PoolList>
            {uniquePools.length === 0 && <div>No active pool memberships</div>}
            {uniquePools.map((pool) => (
              <div
                key={pool.customer_id}
                style={{ padding: '4px 0', color: categoryColor('neutral') }}
              >
                {pool.customer_name || `Client #${pool.customer_id}`}
              </div>
            ))}
          </PoolList>
        </Popover>
      </span>
      {/* GRAVY-10402: renamed from "late" — this badge counts late ARRIVALS
          (check-ins past the grace window), so "late check-ins" says exactly
          what it measures and no longer collides with late cancellations. */}
      <BadgeChip kind={latePositive ? 'positive' : 'warning'}>
        <StatusIcon positive={latePositive} />
        {`${badges.late_count} late check-in${
          badges.late_count === 1 ? '' : 's'
        }`}
      </BadgeChip>
      {/* GRAVY-10402: NEW — all-time late cancellations (late_drop violations),
          the drop counterpart to the late-check-in badge. Warning-styled at 1+,
          affirmative at 0, like the other all-time badges. */}
      <BadgeChip kind={lateCancelsPositive ? 'positive' : 'warning'}>
        <StatusIcon positive={lateCancelsPositive} />
        {`${lateCancelsCount} late cancel${lateCancelsCount === 1 ? '' : 's'}`}
      </BadgeChip>
    </Row>
  );
};

export default StatusBadgeRow;
